import { Dispatch, SetStateAction } from 'react'
import { Check } from '@mui/icons-material'
import {
  Box,
  ListSubheader,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useStytchIsAuthorized } from '@stytch/react/b2b'
import { QueryKey, useQueryClient } from '@tanstack/react-query'
import { useAddOrRemoveProjectTagsWithDocChange } from '@/hooks/doc-changes-wrapper-hooks'
import { OverlayState } from '@/hooks/useOverlay'
import useRelevantWorkflowState from '@/hooks/useRelevantWorkflowState'
import { useCreateProjectTag } from '@/service-library/hooks/project-tags'
import queryKeys from '@/service-library/query-keys'
import { Document } from '@/types/documents'
import { ProjectTag } from '@/types/project-tags'
import { Project } from '@/types/projects'
import { showErrorSnackbar } from '@/utils/snackbars'
import generateUuid from '@/utils/uuid'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import { useSelectedWorkflowContext } from '@/components/workflows/SelectedWorkflowProvider'
import TagChip from './TagChip'

type TagSelectMenuProps = {
  selectedProjectTags: ProjectTag[]
  onTagClick: (tag: ProjectTag) => void
  overlay: OverlayState
  searchText: string
  setSearchText: Dispatch<SetStateAction<string>>
  document?: Document
  documentQueryKey?: QueryKey
}

export default function TagSelectMenu({
  selectedProjectTags,
  onTagClick,
  overlay,
  searchText,
  setSearchText,
  document,
  documentQueryKey,
}: TagSelectMenuProps) {
  const queryClient = useQueryClient()
  const { project, queryKey: projectQueryKey } = useProjectContext()

  const { isAuthorized: canEditProject } = useStytchIsAuthorized(
    'pd.project',
    'edit',
  )

  const visibleTags = project.tags?.filter((tag) => {
    return (
      !searchText || tag.name.toLowerCase().includes(searchText.toLowerCase())
    )
  })

  const visibleSelectedTags =
    visibleTags?.filter((tag) =>
      selectedProjectTags.some((t) => t.id === tag.id),
    ) || []

  const { selectedWorkflow } = useSelectedWorkflowContext()
  const getRelevantWorkflowState = useRelevantWorkflowState()
  const relevantWorkflowState = getRelevantWorkflowState(document)

  const { addOrRemoveProjectTags } = useAddOrRemoveProjectTagsWithDocChange({
    documentId: document?.id || '',
    workflowId: selectedWorkflow.id,
    workflowStateId: relevantWorkflowState?.id || '',
    detailQueryKey: documentQueryKey,
  })

  const { createProjectTag } = useCreateProjectTag({
    sideEffectQueryKeys: [projectQueryKey, queryKeys.documents.all],
    onMutate: async (projectTag) => {
      await queryClient.cancelQueries({ queryKey: projectQueryKey })

      queryClient.setQueryData<Project>(projectQueryKey, (prev) =>
        prev
          ? {
              ...prev,
              tags: [...(prev.tags || []), projectTag],
            }
          : prev,
      )
    },
    onSuccess: (projectTag) => {
      addOrRemoveProjectTags([
        {
          op: 'add',
          project_tag_id: projectTag.id,
        },
      ])
    },
    onError: () => {
      showErrorSnackbar(
        'Failed to create tag. Please contact support if this problem persists.',
      )
    },
  })

  const createTagFromSearchText = () => {
    if (canEditProject && document?.id) {
      createProjectTag({
        id: generateUuid(),
        color: 'blue',
        name: searchText,
        project_id: project.id,
      })
    }
  }
  const onSearchSubmit = () => {
    if (visibleTags?.length) {
      onTagClick(visibleTags[0])
    } else {
      createTagFromSearchText()
    }
  }

  return (
    <Menu
      id="tag-menu"
      anchorEl={overlay.anchorEl as Element}
      open={overlay.isOpen}
      onClose={() => {
        overlay.close()
      }}
      onTransitionExited={() => {
        setSearchText('')
      }}
    >
      <form
        // Wrapping with a form allows submitting by pressing enter
        tabIndex={-1}
        onSubmit={(e) => {
          e.preventDefault()
          onSearchSubmit?.()
        }}
      >
        <TextField
          autoFocus
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value)
          }}
          onKeyDown={(e) => {
            if (e.code === 'Escape') return
            e.stopPropagation() // Prevents the text field to lose focus when typing
          }}
          placeholder="Search"
          variant="outlined"
          size="small"
          sx={{ mx: 2, mt: 1 }}
        />
      </form>
      <ListSubheader
        key="menu-subheader-category"
        sx={{ background: 'transparent', lineHeight: '1rem', mb: 1, mt: 2 }}
      >
        Tags
      </ListSubheader>
      {visibleTags?.length === 0 && (
        <MenuItem disabled>
          <i>None</i>
        </MenuItem>
      )}
      {visibleTags?.map((tag) => {
        const existingDocTag = selectedProjectTags.find((t) => t.id === tag.id)
        return (
          <MenuItem
            tabIndex={0}
            key={tag.id}
            onClick={() => {
              onTagClick(tag)
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ height: 24 }}
            >
              {existingDocTag ? (
                <Check fontSize="small" />
              ) : visibleSelectedTags.length > 0 ? (
                <Box
                  sx={{
                    width: 20,
                  }}
                />
              ) : null}
              <TagChip tag={tag} />
            </Stack>
          </MenuItem>
        )
      })}
      {!visibleTags?.length && searchText && document?.id && (
        <MenuItem
          sx={{ mt: 1, px: 2.5 }}
          onClick={() => {
            createTagFromSearchText()
          }}
        >
          <Stack spacing={0.5}>
            <Typography variant="caption">Create New Tag:</Typography>
            <Box display="flex">
              <TagChip
                tag={{
                  name: searchText,
                  color: 'blue',
                }}
              />
            </Box>
          </Stack>
        </MenuItem>
      )}
    </Menu>
  )
}
